import { ApiClientError } from 'grabbitbot-legacy-ui'
import { apiClient } from './client'
import { stripSecrets } from '../lib/redact'
import type { LegacySoftError } from './types'

/**
 * Thin wrappers over the shared ApiClient that every domain call goes
 * through, so two invariants hold for the WHOLE app, not per page:
 *
 * 1. **No secret reaches component state** (audit §10 D2/D3): every
 *    response is passed through `stripSecrets` — a field whose NAME looks
 *    secret (stripeKey, *Password, token, …) is dropped on arrival, even if
 *    a regressed backend sends it. Covered by src/security-invariants.test.ts
 *    (canary) and the per-page canary tests.
 * 2. **Legacy soft errors are errors**: the legacy controllers answer some
 *    refusals as HTTP 200 `{"error": "..."}` (e.g. b7.tcum.crud
 *    `chat_belongs_to_another_usermanager`, b7.connections.telegram
 *    `Data is NOT from Telegram`). `softErrors: true` turns those into an
 *    `ApiClientError(200, code)` so a page never shows them as success.
 */

interface CallOptions {
  query?: Record<string, string | number | boolean | undefined | null>
  headers?: Record<string, string>
  invalidates?: (string | RegExp)[]
  cache?: boolean
  softErrors?: boolean
  emptyArrayIsRefusal?: boolean
}

function isSoftError(body: unknown): body is LegacySoftError {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return false
  const err = (body as { error?: unknown }).error
  if (typeof err !== 'string') return false
  // `{"error": "chat_belongs_to_another_project"}`, `{"id": …, "error":
  // "cant_expire"}` (expire), and the bare `{"error": ""}` connect refusal.
  return err !== '' || Object.keys(body as object).length === 1
}

function finish<T>(body: unknown, opts: CallOptions): T {
  if (opts.softErrors && isSoftError(body)) {
    throw new ApiClientError(body.message || body.error || 'refused', 200, body.error || 'refused', body)
  }
  // Admin-on-a-user routes answer a NON-admin member with `200 []` instead
  // of 403 (contract: admin credit add/remove, admin payment routes) — a
  // write that "succeeded" with an empty array did nothing.
  if (opts.emptyArrayIsRefusal && Array.isArray(body) && body.length === 0) {
    throw new ApiClientError('Admin access to this project is required.', 403, 'not_project_admin', body)
  }
  return stripSecrets(body) as T
}

// Every project-scoped write can change what any cached GET of that project
// returns (catalogue, balances, subscriptions, transactions) — invalidate
// the whole project subtree rather than guess a narrower pattern per call.
function defaultInvalidation(path: string): (string | RegExp)[] {
  const m = /^\/projects\/[^/]+/.exec(path)
  return m ? [m[0]] : [path]
}

export async function httpGet<T>(path: string, opts: CallOptions = {}): Promise<T> {
  const body = await apiClient.get<unknown>(path, { query: opts.query, headers: opts.headers, cache: opts.cache ?? false })
  return finish<T>(body, opts)
}

export async function httpPost<T>(path: string, data?: unknown, opts: CallOptions = {}): Promise<T> {
  const body = await apiClient.post<unknown>(path, data, {
    query: opts.query,
    headers: opts.headers,
    invalidates: opts.invalidates ?? defaultInvalidation(path),
  })
  return finish<T>(body, opts)
}

export async function httpPatch<T>(path: string, data?: unknown, opts: CallOptions = {}): Promise<T> {
  const body = await apiClient.patch<unknown>(path, data, {
    query: opts.query,
    headers: opts.headers,
    invalidates: opts.invalidates ?? defaultInvalidation(path),
  })
  return finish<T>(body, opts)
}

export async function httpDelete<T>(path: string, opts: CallOptions = {}): Promise<T> {
  const body = await apiClient.delete<unknown>(path, {
    query: opts.query,
    headers: opts.headers,
    invalidates: opts.invalidates ?? defaultInvalidation(path),
  })
  return finish<T>(body, opts)
}

export const enc = encodeURIComponent
